import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Chip, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { Check, ExpandMore, LinkOff } from '@mui/icons-material';
import useSWR from 'swr';
import SparaSwitch from 'components/SparaSwitch/SparaSwitch';
import SparaButton from 'components/SparaButton/SparaButton';
import CustomInput from 'components/CustomInput/CustomInput';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import { quietFetcher, groupApi } from 'lib/api';
import api from 'lib/api';
import kjarniLogo from 'assets/img/kjarni-logo.svg';
import styles from './GroupHrIntegration.module.scss';

const COOLDOWN_MS = 10 * 60 * 1000;
const VISIBLE_RUNS = 5;
const PAY_UNION_RESOURCE = 'employeedetailunions';

const PROVIDERS = [
    {
        value: '',
        icon: LinkOff,
        labelKey: 'group.hr_provider_none',
        descKey: 'group.hr_provider_none_info',
    },
    {
        value: 'corus',
        logo: kjarniLogo,
        labelKey: 'group.hr_provider_kjarni',
        descKey: 'group.hr_provider_kjarni_info',
    },
];

const formatRunDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(
        date.getHours()
    )}:${pad(date.getMinutes())}`;
};

const RUN_STATUS_CLASS = {
    queued: 'statusQueued',
    running: 'statusRunning',
    success: 'statusSuccess',
    failed: 'statusFailed',
    blocked: 'statusBlocked',
    skipped: 'statusSkipped',
};

const FREQUENCIES = [
    { value: '', labelKey: 'group.hr_schedule_none' },
    { value: 'daily', labelKey: 'group.hr_schedule_daily' },
    { value: 'weekly', labelKey: 'group.hr_schedule_weekly' },
    { value: 'monthly', labelKey: 'group.hr_schedule_monthly' },
];

const HR_URL_RE = /^(?:https:\/\/)?([a-z0-9-]+)-api\.starfsmenn\.is\/?$/i;

const parseHrSlug = (value) => {
    const raw = (value || '').trim();
    if (!raw) return '';
    const match = raw.match(HR_URL_RE);
    if (match) return match[1].toLowerCase();
    try {
        const host = new URL(raw.includes('://') ? raw : `https://${raw}`).hostname;
        const hostMatch = host.match(/^([a-z0-9-]+)-api\.starfsmenn\.is$/i);
        if (hostMatch) return hostMatch[1].toLowerCase();
    } catch {
        // Treat as a bare slug below.
    }
    if (/[:/.]/.test(raw)) return '';
    return raw.toLowerCase().replace(/[^a-z0-9-]/g, '');
};

const toHrBaseUrl = (slug) => (slug ? `https://${slug}-api.starfsmenn.is` : '');

const payUnionIdsFromRules = (rules) => {
    if (!Array.isArray(rules)) return [];
    const rule = rules.find((item) => item && item.resource === PAY_UNION_RESOURCE);
    const where = Array.isArray(rule?.where) ? rule.where : [];
    const condition = where.find((item) => item && item.field === 'PayUnionID') || where[0];
    const values = Array.isArray(condition?.values) ? condition.values : [];
    return values.map((value) => String(value)).filter(Boolean);
};

const currentOnlyFromRules = (rules) => {
    if (!Array.isArray(rules)) return true;
    const rule = rules.find((item) => item && item.resource === PAY_UNION_RESOURCE);
    if (!rule || rule.current_only === undefined || rule.current_only === null) return true;
    return !!rule.current_only;
};

const includeRulesPayload = (ids, currentOnly) => {
    if (!ids.length) return [];
    return [
        {
            resource: PAY_UNION_RESOURCE,
            where: [{ field: 'PayUnionID', op: 'in', values: ids }],
            current_only: currentOnly !== false,
        },
    ];
};

const emptyLocal = {
    provider: '',
    enabled: false,
    base_url: '',
    username: '',
    password: '',
    schedule_frequency: '',
    scheduled_time: '02:00',
    scheduled_weekday: 1,
    scheduled_monthday: 1,
    pay_union_ids: [],
    current_only: true,
};

const GroupHrIntegration = forwardRef(function GroupHrIntegration(
    { groupId, integration, isGolden, onDirty, onSave, saving, t, canEdit = false },
    ref
) {
    const [form, setForm] = useState(emptyLocal);
    const [syncOpen, setSyncOpen] = useState(false);
    const [forceOpen, setForceOpen] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [cooldownUntil, setCooldownUntil] = useState(0);
    const [now, setNow] = useState(Date.now());
    const [editingConnection, setEditingConnection] = useState(false);
    const [pollRuns, setPollRuns] = useState(false);

    const { data: runsData, mutate: mutateRuns } = useSWR(
        groupId ? groupApi.hrSyncRuns(groupId) : null,
        quietFetcher,
        {
            refreshInterval: pollRuns ? 2000 : 0,
            refreshWhenHidden: true,
        }
    );

    const runs = Array.isArray(runsData?.results) ? runsData.results : [];
    const hasActiveRun = runs.some(
        (run) => run.status === 'queued' || run.status === 'running'
    );

    useEffect(() => {
        setPollRuns(hasActiveRun);
    }, [hasActiveRun]);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            provider: integration?.provider || '',
            enabled: !!integration?.enabled,
            base_url: integration?.base_url || '',
            username: '',
            password: '',
            schedule_frequency: integration?.schedule_frequency || '',
            scheduled_time: (integration?.scheduled_time || '02:00').slice(0, 5),
            scheduled_weekday: integration?.scheduled_weekday || 1,
            scheduled_monthday: integration?.scheduled_monthday || 1,
            pay_union_ids: payUnionIdsFromRules(integration?.include_rules),
            current_only: currentOnlyFromRules(integration?.include_rules),
        }));
    }, [integration]);

    const patch = (next) => {
        if (!canEdit) return;
        if (onDirty) onDirty();
        setForm((prev) => ({ ...prev, ...next }));
    };

    useImperativeHandle(ref, () => ({
        getPayload: () => {
            if (!canEdit || isGolden || !groupId) {
                return undefined;
            }
            if (!form.provider) {
                return null;
            }
            const baseUrl = toHrBaseUrl(parseHrSlug(form.base_url));
            const nextUrlChanged = baseUrl !== (integration?.base_url || '').trim();
            const nextUsernameChanging = !!(form.username || '').trim();
            const usernameAfterSave = nextUsernameChanging || !!integration?.username_set;
            const passwordAfterSave =
                !!form.password ||
                (!!integration?.password_set && !nextUrlChanged && !nextUsernameChanging);
            const payload = {
                provider: form.provider,
                enabled: !!form.enabled && usernameAfterSave && passwordAfterSave,
                base_url: baseUrl,
                schedule_frequency: form.schedule_frequency || null,
                scheduled_time: form.schedule_frequency ? form.scheduled_time : null,
                scheduled_weekday:
                    form.schedule_frequency === 'weekly' ? Number(form.scheduled_weekday) : null,
                scheduled_monthday:
                    form.schedule_frequency === 'monthly' ? Number(form.scheduled_monthday) : null,
                include_rules: includeRulesPayload(
                    form.pay_union_ids || [],
                    form.current_only !== false
                ),
            };
            if (form.username) {
                payload.username = form.username;
            }
            if (form.password) {
                payload.password = form.password;
            }
            return payload;
        },
    }));

    const lastManual = useMemo(() => runs.find((run) => run.trigger === 'manual'), [runs]);
    const latestRun = runs[0];
    const visibleRuns = runs.slice(0, VISIBLE_RUNS);
    const olderRuns = runs.slice(VISIBLE_RUNS);

    const cooldownLeftMs = useMemo(() => {
        const times = [cooldownUntil];
        if (lastManual?.created_at) {
            times.push(new Date(lastManual.created_at).getTime() + COOLDOWN_MS);
        }
        const until = Math.max(...times.filter(Boolean), 0);
        return Math.max(0, until - now);
    }, [cooldownUntil, lastManual, now]);

    const slug = parseHrSlug(form.base_url);
    const savedSlug = parseHrSlug(integration?.base_url);
    const credentialsSavedInDb = !!integration?.username_set && !!integration?.password_set;
    const connectionComplete =
        !!integration?.provider && !!savedSlug && credentialsSavedInDb;
    const connectionKey = [
        integration?.provider || '',
        integration?.base_url || '',
        integration?.username_set ? '1' : '0',
        integration?.password_set ? '1' : '0',
    ].join('|');
    const usernameChanging = !!(form.username || '').trim();
    const urlChanged = toHrBaseUrl(slug) !== (integration?.base_url || '').trim();
    const needsPasswordAgain = (urlChanged || usernameChanging) && !form.password;
    const credentialsLookSaved =
        credentialsSavedInDb && !usernameChanging && !form.password && !urlChanged;
    const showConnectionEditor = canEdit && (!connectionComplete || editingConnection);
    const showEnableSwitch = credentialsSavedInDb && !needsPasswordAgain;
    const showSync =
        form.enabled && integration?.enabled && credentialsSavedInDb && !needsPasswordAgain;

    const canFetchPayUnions =
        canEdit &&
        !!groupId &&
        form.provider === 'corus' &&
        credentialsSavedInDb &&
        !urlChanged;

    const {
        data: payUnionsData,
        error: payUnionsError,
        isValidating: payUnionsLoading,
    } = useSWR(canFetchPayUnions ? groupApi.hrSyncPayUnions(groupId) : null, quietFetcher, {
        shouldRetryOnError: false,
        revalidateOnFocus: false,
    });

    const payUnionOptions = useMemo(() => {
        const list = Array.isArray(payUnionsData?.results) ? payUnionsData.results : [];
        const byId = new Map();
        list.forEach((item) => {
            if (!item || item.id === undefined || item.id === null) return;
            const id = String(item.id);
            byId.set(id, { id, name: item.name || id });
        });
        (form.pay_union_ids || []).forEach((id) => {
            const key = String(id);
            if (!byId.has(key)) {
                byId.set(key, { id: key, name: key });
            }
        });
        return Array.from(byId.values());
    }, [payUnionsData, form.pay_union_ids]);

    const payUnionValue = useMemo(
        () =>
            (form.pay_union_ids || []).map(
                (id) =>
                    payUnionOptions.find((option) => String(option.id) === String(id)) || {
                        id: String(id),
                        name: String(id),
                    }
            ),
        [form.pay_union_ids, payUnionOptions]
    );

    const savedPayUnionIds = payUnionIdsFromRules(integration?.include_rules);
    const savedCurrentOnly = currentOnlyFromRules(integration?.include_rules);
    const filterDirty =
        JSON.stringify([...(form.pay_union_ids || [])].sort()) !==
            JSON.stringify([...savedPayUnionIds].sort()) ||
        (form.current_only !== false) !== savedCurrentOnly;

    const payUnionsErrorCode = payUnionsError?.response?.data?.results?.error_code;
    const payUnionsErrorKey = payUnionsErrorCode
        ? `group.hr_pay_unions_error_${payUnionsErrorCode}`
        : 'group.hr_pay_unions_error';
    const payUnionsErrorText = payUnionsError
        ? t(payUnionsErrorKey) === payUnionsErrorKey
            ? t('group.hr_pay_unions_error')
            : t(payUnionsErrorKey)
        : '';

    const runInFlight = ['queued', 'running'].includes(latestRun?.status);
    const canSync =
        !!groupId && !!form.provider && showSync && !syncing && !isGolden && !runInFlight;

    const startSync = async (force) => {
        setSyncOpen(false);
        setForceOpen(false);
        if (!groupId) return;
        setSyncing(true);
        setPollRuns(true);
        try {
            const response = await api.post(groupApi.hrSync(groupId), { force: !!force });
            const created = response?.data?.results;
            setCooldownUntil(Date.now() + COOLDOWN_MS);
            if (created?.id) {
                await mutateRuns(
                    (current) => {
                        const existing = Array.isArray(current?.results) ? current.results : [];
                        return {
                            ...(current || {}),
                            results: [created, ...existing.filter((run) => run.id !== created.id)],
                        };
                    },
                    { revalidate: true }
                );
            } else {
                await mutateRuns();
            }
        } catch (error) {
            const retry = Number(
                error?.response?.headers?.['retry-after'] ||
                    error?.response?.data?.results?.retry_after ||
                    0
            );
            if (error?.response?.status === 429) {
                setCooldownUntil(Date.now() + (retry ? retry * 1000 : COOLDOWN_MS));
            }
        } finally {
            setSyncing(false);
        }
    };

    const selectProvider = (value) => {
        patch({
            provider: value,
            enabled: value ? form.enabled : false,
            pay_union_ids: value ? form.pay_union_ids : [],
        });
    };

    useEffect(() => {
        setEditingConnection(!connectionComplete);
    }, [connectionKey, connectionComplete]);

    const startEditConnection = () => setEditingConnection(true);

    const cancelEditConnection = () => {
        setForm((prev) => ({
            ...prev,
            provider: integration?.provider || '',
            base_url: integration?.base_url || '',
            username: '',
            password: '',
            pay_union_ids: payUnionIdsFromRules(integration?.include_rules),
            current_only: currentOnlyFromRules(integration?.include_rules),
        }));
        setEditingConnection(false);
    };

    const renderRun = (run) => {
        const statusClass = styles[RUN_STATUS_CLASS[run.status]] || styles.statusQueued;
        return (
            <div key={run.id} className={styles.runCard}>
                <div className={styles.runTop}>
                    <div>
                        <div className={styles.runDate}>{formatRunDate(run.created_at)}</div>
                        <div className={styles.runMeta}>
                            {run.trigger === 'scheduled'
                                ? t('group.hr_trigger_scheduled')
                                : t('group.hr_trigger_manual')}
                            {run.forced ? ` · ${t('group.hr_run_forced')}` : ''}
                        </div>
                    </div>
                    <span className={`${styles.statusPill} ${statusClass}`}>
                        {t(`group.hr_run_status_${run.status}`)}
                    </span>
                </div>
                <div className={styles.runStats}>
                    <span className={styles.statAdded}>
                        +{run.added_count} {t('group.hr_run_added')}
                    </span>
                    <span className={styles.statRemoved}>
                        −{run.removed_count} {t('group.hr_run_removed')}
                    </span>
                    <span className={styles.statSkipped}>
                        {run.skipped_count} {t('group.hr_run_skipped')}
                    </span>
                </div>
                {run.error_message ? <div className={styles.error}>{run.error_message}</div> : null}
                {run.warning_code === 'shrink_warning' ? (
                    <div className={styles.warning}>{t('group.hr_shrink_warning')}</div>
                ) : null}
            </div>
        );
    };

    if (isGolden) {
        return <p className={styles.help}>{t('group.hr_golden_disabled')}</p>;
    }

    if (!groupId) {
        return <p className={styles.help}>{t('group.hr_save_first')}</p>;
    }

    if (!canEdit && !connectionComplete) {
        return (
            <div className={styles.section}>
                <p className={styles.help}>{t('group.hr_not_configured')}</p>
                <p className={styles.help}>{t('group.hr_not_configured_info')}</p>
            </div>
        );
    }

    return (
        <div className={styles.section}>
            {integration?.has_group_access_token ? (
                <p className={styles.warning}>{t('group.hr_access_token_warning')}</p>
            ) : null}

            {showConnectionEditor ? (
                <div
                    className={styles.providerGrid}
                    role="radiogroup"
                    aria-label={t('group.hr_provider')}
                >
                {PROVIDERS.map((provider) => {
                    const Icon = provider.icon;
                    const selected = form.provider === provider.value;
                    return (
                        <button
                            key={provider.value || 'none'}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            className={`${styles.providerCard} ${
                                provider.logo ? styles.logoCard : ''
                            } ${selected ? styles.selected : ''}`}
                            onClick={() => selectProvider(provider.value)}
                        >
                            <div className={styles.providerHeader}>
                                {provider.logo ? (
                                    <img
                                        src={provider.logo}
                                        alt={t(provider.labelKey)}
                                        className={styles.providerLogo}
                                    />
                                ) : (
                                    <Icon className={styles.providerIcon} />
                                )}
                                {selected ? (
                                    <span className={styles.providerCheck}>
                                        <Check fontSize="inherit" />
                                    </span>
                                ) : null}
                            </div>
                            <div className={styles.providerBody}>
                                <span className={styles.providerName}>{t(provider.labelKey)}</span>
                                <span className={styles.providerDesc}>{t(provider.descKey)}</span>
                            </div>
                        </button>
                    );
                })}
                </div>
            ) : null}

            {form.provider ? (
                <>
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <h4 className={styles.panelTitle}>{t('group.hr_connection')}</h4>
                            {canEdit && connectionComplete && !showConnectionEditor ? (
                                <SparaButton
                                    type="button"
                                    variant="outlined"
                                    size="small"
                                    onClick={startEditConnection}
                                >
                                    {t('group.hr_edit_connection')}
                                </SparaButton>
                            ) : null}
                            {canEdit && connectionComplete && showConnectionEditor ? (
                                <SparaButton
                                    type="button"
                                    variant="outlined"
                                    size="small"
                                    onClick={cancelEditConnection}
                                >
                                    {t('group.hr_cancel_edit')}
                                </SparaButton>
                            ) : null}
                        </div>
                        {showConnectionEditor ? (
                            <>
                                <div className={styles.field}>
                                    <p className={styles.fieldLabel}>{t('group.hr_base_url')}</p>
                                    <div className={styles.urlField}>
                                        <span className={styles.urlAffix}>https://</span>
                                        <input
                                            className={styles.urlInput}
                                            value={slug}
                                            placeholder="vinbudin"
                                            autoComplete="off"
                                            autoCapitalize="off"
                                            spellCheck={false}
                                            size={Math.max(10, slug.length + 1)}
                                            onChange={(e) =>
                                                patch({
                                                    base_url: toHrBaseUrl(
                                                        parseHrSlug(e.target.value)
                                                    ),
                                                    pay_union_ids: [],
                                                })
                                            }
                                        />
                                        <span className={styles.urlAffix}>-api.starfsmenn.is</span>
                                    </div>
                                    <p className={styles.help}>{t('group.hr_base_url_info')}</p>
                                </div>

                                <div
                                    className={`${styles.notice} ${
                                        credentialsLookSaved
                                            ? styles.noticeSuccess
                                            : styles.noticePending
                                    }`}
                                >
                                    {credentialsLookSaved
                                        ? t('group.hr_credentials_saved')
                                        : t('group.hr_credentials_needed')}
                                </div>

                                <CustomInput
                                    labelText={t('group.hr_username')}
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        value: form.username,
                                        autoComplete: 'new-password',
                                        placeholder:
                                            integration?.username_set && !form.username
                                                ? t('group.hr_saved_placeholder')
                                                : '',
                                        onChange: (e) => patch({ username: e.target.value }),
                                    }}
                                />
                                <CustomInput
                                    labelText={t('password')}
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        type: 'password',
                                        value: form.password,
                                        autoComplete: 'new-password',
                                        placeholder:
                                            integration?.password_set && !form.password
                                                ? t('group.hr_saved_placeholder')
                                                : '',
                                        onChange: (e) => patch({ password: e.target.value }),
                                    }}
                                />
                                {needsPasswordAgain ? (
                                    <p className={styles.warning}>
                                        {t('group.hr_password_reenter_info')}
                                    </p>
                                ) : null}
                            </>
                        ) : (
                            <>
                                <div className={styles.summary}>
                                    <img
                                        src={kjarniLogo}
                                        alt={t('group.hr_provider_kjarni')}
                                        className={styles.summaryLogo}
                                    />
                                    <p className={styles.summaryLine}>
                                        {savedSlug}-api.starfsmenn.is
                                    </p>
                                </div>
                                <div className={`${styles.notice} ${styles.noticeSuccess}`}>
                                    {t('group.hr_connection_ready')}
                                </div>
                            </>
                        )}
                    </div>

                    {canEdit ? (
                    <div className={styles.panel}>
                        <h4 className={styles.panelTitle}>{t('group.hr_sync_settings')}</h4>
                        {showEnableSwitch ? (
                            <SparaSwitch
                                label={t('group.hr_enabled')}
                                subtitle={
                                    form.enabled
                                        ? t('group.hr_enabled_info')
                                        : t('group.hr_disabled_info')
                                }
                                checked={!!form.enabled}
                                disabled={!canEdit}
                                onChange={(e) => patch({ enabled: e.target.checked })}
                            />
                        ) : (
                            <p className={styles.help}>
                                {canEdit
                                    ? t('group.hr_save_to_enable')
                                    : t('group.hr_not_configured_info')}
                            </p>
                        )}

                        <div className={`${styles.field} ${styles.payUnions}`}>
                            {canFetchPayUnions ? (
                                <Autocomplete
                                    multiple
                                    options={payUnionOptions}
                                    value={payUnionValue}
                                    loading={payUnionsLoading}
                                    disabled={!canEdit}
                                    isOptionEqualToValue={(option, value) =>
                                        String(option?.id) === String(value?.id)
                                    }
                                    getOptionLabel={(option) =>
                                        option?.name && option.name !== option.id
                                            ? `${option.name} (${option.id})`
                                            : String(option?.name || option?.id || '')
                                    }
                                    onChange={(_, next) =>
                                        patch({
                                            pay_union_ids: (next || []).map((item) =>
                                                String(item.id)
                                            ),
                                        })
                                    }
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                label={option.name || option.id}
                                                {...getTagProps({ index })}
                                                size="small"
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={t('group.hr_pay_unions_placeholder')}
                                        />
                                    )}
                                />
                            ) : (
                                <p className={styles.help}>{t('group.hr_pay_unions_save_first')}</p>
                            )}
                            <p className={styles.help}>{t('group.hr_pay_unions_info')}</p>
                            {payUnionsErrorText ? (
                                <p className={styles.error}>{payUnionsErrorText}</p>
                            ) : null}
                        </div>

                        {canFetchPayUnions || (form.pay_union_ids || []).length ? (
                            <SparaSwitch
                                label={t('group.hr_pay_unions_current_only')}
                                subtitle={t('group.hr_pay_unions_current_only_info')}
                                checked={form.current_only !== false}
                                disabled={!canEdit}
                                onChange={(e) => patch({ current_only: e.target.checked })}
                            />
                        ) : null}

                        {filterDirty && connectionComplete ? (
                            <p className={styles.warning}>{t('group.hr_pay_unions_force_info')}</p>
                        ) : null}

                        <div className={styles.field}>
                            <p className={styles.fieldLabel}>{t('group.hr_schedule_frequency')}</p>
                            <div
                                className={styles.pills}
                                role="radiogroup"
                                aria-label={t('group.hr_schedule_frequency')}
                            >
                                {FREQUENCIES.map((option) => {
                                    const selected = form.schedule_frequency === option.value;
                                    return (
                                        <button
                                            key={option.value || 'none'}
                                            type="button"
                                            role="radio"
                                            aria-checked={selected}
                                            disabled={!canEdit}
                                            className={`${styles.pill} ${
                                                selected ? styles.pillActive : ''
                                            }`}
                                            onClick={() =>
                                                patch({ schedule_frequency: option.value })
                                            }
                                        >
                                            {t(option.labelKey)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {form.schedule_frequency ? (
                            <>
                                <CustomInput
                                    labelText={t('group.hr_scheduled_time')}
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        type: 'time',
                                        value: form.scheduled_time,
                                        disabled: !canEdit,
                                        onChange: (e) => patch({ scheduled_time: e.target.value }),
                                    }}
                                />
                                <p className={styles.help}>{t('group.hr_scheduled_time_info')}</p>
                            </>
                        ) : null}

                        {form.schedule_frequency === 'weekly' ? (
                            <FormControl fullWidth className={styles.field}>
                                <InputLabel shrink>{t('group.hr_scheduled_weekday')}</InputLabel>
                                <Select
                                    value={form.scheduled_weekday}
                                    disabled={!canEdit}
                                    onChange={(e) => patch({ scheduled_weekday: e.target.value })}
                                >
                                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                                        <MenuItem key={day} value={day}>
                                            {t(`group.hr_weekday_${day}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : null}

                        {form.schedule_frequency === 'monthly' ? (
                            <>
                                <CustomInput
                                    labelText={t('group.hr_scheduled_monthday')}
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        type: 'number',
                                        min: 1,
                                        max: 31,
                                        value: form.scheduled_monthday,
                                        disabled: !canEdit,
                                        onChange: (e) =>
                                            patch({
                                                scheduled_monthday: Number(e.target.value),
                                            }),
                                    }}
                                />
                                <p className={styles.help}>
                                    {t('group.hr_scheduled_monthday_info')}
                                </p>
                            </>
                        ) : null}

                        {latestRun?.status === 'blocked' ? (
                            <p className={styles.warning}>{t('group.hr_sync_blocked_info')}</p>
                        ) : null}
                    </div>
                    ) : latestRun?.status === 'blocked' ? (
                        <p className={styles.warning}>{t('group.hr_sync_blocked_info')}</p>
                    ) : null}
                </>
            ) : null}

            {showSync ? (
                <p className={styles.syncPrompt}>{t('group.hr_sync_prompt')}</p>
            ) : null}
            {canEdit || showSync ? (
                <div className={styles.toolbar}>
                    <div className={styles.toolbarStart}>
                        {showSync ? (
                            <SparaButton
                                type="button"
                                variant="outlined"
                                onClick={() => setSyncOpen(true)}
                                disabled={!canSync}
                            >
                                {cooldownLeftMs ? t('group.hr_sync') : t('group.hr_sync_now')}
                            </SparaButton>
                        ) : null}
                        {showSync && latestRun?.status === 'blocked' ? (
                            <SparaButton
                                type="button"
                                color="danger"
                                variant="outlined"
                                onClick={() => setForceOpen(true)}
                                disabled={!canSync}
                            >
                                {t('group.hr_sync_force')}
                            </SparaButton>
                        ) : null}
                    </div>
                    {canEdit && onSave ? (
                        <SparaButton
                            type="button"
                            className={styles.toolbarSave}
                            onClick={onSave}
                            disabled={saving}
                            loading={saving}
                        >
                            {t('group.hr_save')}
                        </SparaButton>
                    ) : null}
                </div>
            ) : null}

            <div className={styles.runsSection}>
                <div className={styles.runsHeader}>
                    <h4 className={styles.runsTitle}>{t('group.hr_sync_runs')}</h4>
                    {runs.length > 0 ? (
                        <span className={styles.runsCount}>{runs.length}</span>
                    ) : null}
                </div>
                {runs.length === 0 ? (
                    <p className={styles.runsEmpty}>{t('group.hr_sync_runs_empty')}</p>
                ) : (
                    <div className={styles.runs}>
                        {visibleRuns.map(renderRun)}
                        {olderRuns.length > 0 ? (
                            <Accordion
                                className={styles.olderRuns}
                                disableGutters
                                elevation={0}
                            >
                                <AccordionSummary
                                    className={styles.olderRunsSummary}
                                    expandIcon={<ExpandMore />}
                                >
                                    {t('group.hr_sync_runs_older')} ({olderRuns.length})
                                </AccordionSummary>
                                <AccordionDetails className={styles.olderRunsDetails}>
                                    <div className={styles.runs}>{olderRuns.map(renderRun)}</div>
                                </AccordionDetails>
                            </Accordion>
                        ) : null}
                    </div>
                )}
            </div>

            <ConfirmDialog
                open={syncOpen}
                title={t('group.hr_sync_now')}
                message={t('group.hr_sync_now_confirm')}
                onConfirm={() => startSync(false)}
                onCancel={() => setSyncOpen(false)}
                confirmText={t('group.hr_sync_now')}
                type="warning"
            />
            <ConfirmDialog
                open={forceOpen}
                title={t('group.hr_sync_force')}
                message={t('group.hr_sync_force_confirm')}
                onConfirm={() => startSync(true)}
                onCancel={() => setForceOpen(false)}
                confirmText={t('group.hr_sync_force')}
                type="danger"
            />
        </div>
    );
});

export default GroupHrIntegration;
